// Color tokens, aligned with ui/docs/simkura-design.md (Stripe-adapted).
// Token NAMES are preserved for back-compat with existing pages; only
// VALUES change. This means a page using theme.background.secondary picks
// up the new canvas-soft tint automatically.

// ── Grayscale (light) ─────────────────────────────────────────────────
// Re-tuned to navy-leaning "ink scale" per the spec. The old neutral
// grays read as warm and clashed with the indigo accent.
//
//   gray12 → ink            (#0d253d) primary text
//   gray11 → ink-secondary  (#273951) body copy
//   gray9  → ink-mute       (#64748d) captions, helper text
//   gray8  → ink-mute-2     (#94a3b8) placeholder text
//   gray6  → hairline-strong(#cbd5e0) hovered card outline
//   gray5  → hairline       (#e3e8ee) default card border
//   gray4  → canvas-tint    (#f0f4f8) row hover
//   gray2  → canvas-soft    (#f6f9fc) page background
//   gray1  → canvas         (#ffffff) card / modal body
export const GRAY_SCALE_LIGHT = {
  gray0:  '#000000',
  gray1:  '#ffffff',  // canvas
  gray2:  '#f6f9fc',  // canvas-soft (page bg)
  gray3:  '#f4f7fb',
  gray4:  '#f0f4f8',  // canvas-tint (row hover)
  gray5:  '#e3e8ee',  // hairline
  gray6:  '#cbd5e0',  // hairline-strong
  gray7:  '#a8c3de',  // hairline-input
  gray8:  '#94a3b8',  // ink-mute-2 (placeholders)
  gray9:  '#64748d',  // ink-mute (captions)
  gray10: '#3a4a63',
  gray11: '#273951',  // ink-secondary
  gray12: '#0d253d',  // ink (primary text)
};

// Dark grayscale untouched — dark mode is an acknowledged gap (see
// known-gaps in simkura-design.md). Values left as-is so the structural
// types still resolve; visuals will need a dedicated pass later.
export const GRAY_SCALE_DARK = {
  gray0:  '#000000',
  gray1:  '#111111',
  gray2:  '#191919',
  gray3:  '#222222',
  gray4:  '#292929',
  gray5:  '#2F2F2F',
  gray6:  '#353535',
  gray7:  '#3E3E3E',
  gray8:  '#505050',
  gray9:  '#606060',
  gray10: '#6E6E6E',
  gray11: '#989898',
  gray12: '#EBEBEB',
};

// ── Accent (indigo) ───────────────────────────────────────────────────
// Stripe-flavored indigo. More saturated than the old #3E63DD; reads as
// brand. Reserved for CTAs, focus rings, active pill states, links.
export const ACCENT_LIGHT = {
  primary:    '#533afd',
  secondary:  '#533afd',
  tertiary:   '#eef0ff',  // primary-bg-soft (focus shadow base, soft pill bg)
  quaternary: '#dbe0ff',
  accent10:   '#4434d4',  // primary-deep (hover/pressed)
};

export const ACCENT_DARK = {
  primary:    '#7e6dff',
  secondary:  '#7e6dff',
  tertiary:   '#2a2354',
  quaternary: '#1f1a40',
  accent10:   '#6754ee',
};

// ── Semantic foreground colors ────────────────────────────────────────
// Muted versions vs. the old eye-popping primaries. These read as
// "business/admin" rather than "alert/dashboard demo".
export const SEMANTIC_COLORS = {
  red:    '#c0282d',  // critical
  green:  '#0e8a4f',  // positive
  yellow: '#9a6700',  // warning (was #F5D90A — too acid for an admin UI)
  orange: '#9a4500',
  blue:   '#3b5fde',  // info
};

// Paired soft backgrounds for chip variants. Keep these as a separate
// export so chip code can pull `bg` and `fg` from tokens directly.
export const SEMANTIC_BACKGROUNDS = {
  positive: '#dcfce7',
  warning:  '#fef3c7',
  critical: '#fee2e2',
  info:     '#e0e7ff',
  neutral:  '#f0f4f8',  // matches canvas-tint
};
